import { NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { DevicesV2ControllerFindAllStatusItem } from '@api/devices';
import { type NormalizedDeviceData } from '@devices';

import { SystemDeviceTypesIds } from '../../models/device-types';

export enum DeviceDisplayStatus {
  ARCHIVED = 'archived',
  OFFLINE = 'offline',
  ONLINE = 'online',
  PAUSED = 'paused',
}

const DEVICE_OFFLINE_TIMEOUT_SECONDS = 180;
const CAMERA_OFFLINE_TIMEOUT_SECONDS = 86_460;

export function computeDeviceDisplayStatus(device: {
  deviceTypeId: string;
  latestStreamingTimestamp: string | null;
  status: DevicesV2ControllerFindAllStatusItem;
}) {
  if (device.status === DevicesV2ControllerFindAllStatusItem.PAUSED) {
    return DeviceDisplayStatus.PAUSED;
  }

  if (device.status === DevicesV2ControllerFindAllStatusItem.ARCHIVED) {
    return DeviceDisplayStatus.ARCHIVED;
  }

  if (!device.latestStreamingTimestamp) {
    return DeviceDisplayStatus.OFFLINE;
  }

  const timeoutSeconds = device.deviceTypeId === SystemDeviceTypesIds.Camera
    ? CAMERA_OFFLINE_TIMEOUT_SECONDS
    : DEVICE_OFFLINE_TIMEOUT_SECONDS;
  const staleSeconds = (Date.now() - new Date(device.latestStreamingTimestamp).getTime()) / 1000;

  return staleSeconds >= timeoutSeconds ? DeviceDisplayStatus.OFFLINE : DeviceDisplayStatus.ONLINE;
}

const LABEL: Record<DeviceDisplayStatus, string> = {
  [DeviceDisplayStatus.ARCHIVED]: 'Archived',
  [DeviceDisplayStatus.OFFLINE]: 'Offline',
  [DeviceDisplayStatus.ONLINE]: 'Online',
  [DeviceDisplayStatus.PAUSED]: 'Paused',
};

@Component({
  imports: [NgClass],
  selector: 'avs-device-display-status',
  standalone: true,
  styleUrl: './device-display-status.component.scss',
  template: `<span
    class="status-block"
    [ngClass]="{
      'status-archived': status() === eDeviceDisplayStatus.ARCHIVED,
      'status-offline': status() === eDeviceDisplayStatus.OFFLINE,
      'status-online': status() === eDeviceDisplayStatus.ONLINE,
      'status-paused': status() === eDeviceDisplayStatus.PAUSED,
    }"
    >{{ label() }}</span
  >`,
})
export class DeviceDisplayStatusComponent {
  device = input.required<NormalizedDeviceData>();

  protected readonly eDeviceDisplayStatus = DeviceDisplayStatus;
  protected status = computed(() => computeDeviceDisplayStatus(this.device()));
  protected label = computed(() => LABEL[this.status()]);
}
